import { useEffect, useState } from "react";
import { toast, type Id } from "react-toastify";
import { getApiErrorMessage } from "@/utils/apiErrors";

export const DEFERRED_DELETE_UNDO_SECONDS = 5;

type PendingJob = {
  timeoutId: ReturnType<typeof setTimeout>;
  toastId: Id;
  onUndo?: () => void;
};

const pendingJobs = new Map<string, PendingJob>();

export function cancelDeferredDelete(idKey: string): void {
  const job = pendingJobs.get(idKey);
  if (!job) return;
  clearTimeout(job.timeoutId);
  toast.dismiss(job.toastId);
  pendingJobs.delete(idKey);
  job.onUndo?.();
}

function PendingDeleteToast({
  label,
  hint,
  secondsLeft,
  onUndo,
}: {
  label: string;
  hint?: string;
  secondsLeft: number;
  onUndo: () => void;
}) {
  return (
    <div className="flex min-w-[240px] max-w-sm flex-col gap-2 py-0.5 text-sm text-slate-800">
      <span>
        Deleting <span className="font-semibold">{label}</span> in {secondsLeft}s…
      </span>
      {hint ? <span className="text-xs text-slate-600">{hint}</span> : null}
      <button
        type="button"
        onClick={onUndo}
        className="w-fit font-semibold text-bridge-accent underline hover:text-bridge-accentSoft"
      >
        Undo
      </button>
    </div>
  );
}

function CountdownToastContent({
  idKey,
  label,
  hint,
}: {
  idKey: string;
  label: string;
  hint?: string;
}) {
  const [secondsLeft, setSecondsLeft] = useState(DEFERRED_DELETE_UNDO_SECONDS);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <PendingDeleteToast
      label={label}
      hint={hint}
      secondsLeft={secondsLeft}
      onUndo={() => {
        cancelDeferredDelete(idKey);
        toast.info("Delete cancelled.", { autoClose: 2500, toastId: "deferred-delete-undo" });
      }}
    />
  );
}

/**
 * Runs {@link onCommit} after {@link DEFERRED_DELETE_UNDO_SECONDS} unless Undo is clicked.
 * The commit timer lives outside React so navigation / toast unmount does not cancel the API call.
 */
export function scheduleDeferredDeleteWithUndo(options: {
  idKey: string;
  label: string;
  hint?: string;
  /** Called immediately when the undo window starts (optimistic UI). */
  onScheduled?: () => void;
  /** Called when the user clicks Undo — restore optimistic UI. */
  onUndo?: () => void;
  /** Called when the API commit fails — restore optimistic UI if needed. */
  onFailed?: () => void;
  onCommit: () => void | Promise<void>;
  onComplete?: () => void;
}): void {
  const { idKey, label, hint, onScheduled, onUndo, onFailed, onCommit, onComplete } = options;

  cancelDeferredDelete(idKey);
  onScheduled?.();

  const toastId = toast.info(<CountdownToastContent idKey={idKey} label={label} hint={hint} />, {
    autoClose: false,
    closeOnClick: false,
    toastId: `deferred-delete-pending-${idKey}`,
  });

  const timeoutId = setTimeout(() => {
    pendingJobs.delete(idKey);
    toast.dismiss(toastId);

    Promise.resolve(onCommit())
      .then(() => {
        onComplete?.();
      })
      .catch((err) => {
        if (onFailed) onFailed();
        else onUndo?.();
        toast.error(getApiErrorMessage(err) ?? "The delete failed. Nothing was removed.", {
          toastId: `deferred-delete-failed-${idKey}`,
        });
      });
  }, DEFERRED_DELETE_UNDO_SECONDS * 1000);

  pendingJobs.set(idKey, { timeoutId, toastId, onUndo });
}
